import React, { useEffect, useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

const riskStyle = {
  HIGH: { bg: "bg-red-50", border: "border-red-400", text: "text-red-700", icon: "🚨", label: "HIGH RISK" },
  MEDIUM: { bg: "bg-amber-50", border: "border-amber-400", text: "text-amber-700", icon: "⚠️", label: "SOME WARNING SIGNS" },
  LOW: { bg: "bg-green-50", border: "border-green-400", text: "text-green-700", icon: "✅", label: "NO OBVIOUS RED FLAGS" },
};

const ScamCheck = () => {
  useDocTitle("ZelT | Scam Check");
  const navigate = useNavigate();
  const [mode, setMode] = useState("text"); // text | image
  const [content, setContent] = useState("");
  const [imageBase64, setImageBase64] = useState(null);
  const [imageName, setImageName] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const verdictRef = React.useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("zelt_token");
    if (!token) navigate("/membership");
  }, [navigate]);

  useEffect(() => {
    if (verdict) verdictRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [verdict]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Notiflix.Notify.failure("Image too large — 5MB maximum.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageBase64(reader.result);
      setImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const analyze = (e) => {
    e.preventDefault();
    if (analyzing) return;
    if (mode === "text" && !content.trim()) return;
    if (mode === "image" && !imageBase64) return;
    setAnalyzing(true);
    setVerdict(null);

    axios
      .post(
        `${API_BASE}/api/scam-check`,
        mode === "image"
          ? { imageBase64 }
          : { content: content.trim() },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("zelt_token")}` },
          timeout: 60000,
        }
      )
      .then((response) => setVerdict(response.data.verdict))
      .catch((error) => {
        Notiflix.Report.failure(
          "Scam Check couldn't analyze",
          error.response?.data?.message || "Please try again in a few minutes.",
          "Okay"
        );
      })
      .finally(() => setAnalyzing(false));
  };

  const v = verdict ? riskStyle[verdict.risk_level] : null;

  return (
    <div>
      <NavBar />
      <div
        className="w-full py-12"
        style={{ background: "linear-gradient(160deg, #0f172a, #1e3a8a)", minHeight: "calc(100vh - 180px)" }}
      >
        <div className="mx-4 max-w-3xl mx-auto">
          {/* header */}
          <div className="text-center mb-8" data-aos="zoom-in">
            <span className="text-5xl">🛡️</span>
            <h1 className="text-4xl font-extrabold text-white mt-3 mb-2">
              Scam Check
            </h1>
            <p className="text-teal-200 max-w-2xl mx-auto">
              Received a suspicious email, text, or pop-up? Get a second
              opinion before you click, pay, or reply.
            </p>
          </div>

          {/* input card */}
          <div
            className="rounded-3xl shadow-2xl overflow-hidden border border-teal-400/30 mb-8"
            data-aos="fade-up"
          >
            <div
              className="p-4 text-white font-bold"
              style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
            >
              ZELTECHNOLOGY SCAM CHECK
            </div>
            <div className="bg-slate-900 p-6">
              {/* mode toggle */}
              <div className="flex flex-wrap gap-3 mb-5">
                <button
                  onClick={() => setMode("text")}
                  className={`px-4 py-2.5 rounded-xl font-bold text-sm transition ${
                    mode === "text"
                      ? "bg-teal-400 text-blue-900"
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  ✉️ Paste Email / Text
                </button>
                <button
                  onClick={() => setMode("image")}
                  className={`px-4 py-2.5 rounded-xl font-bold text-sm transition ${
                    mode === "image"
                      ? "bg-teal-400 text-blue-900"
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  📷 Upload Screenshot
                </button>
              </div>

              <form onSubmit={analyze}>
                {mode === "text" ? (
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Paste the suspicious email or text message here… (Tip: black out any personal details first)"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 text-white placeholder-gray-400 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
                    rows={8}
                    maxLength={4000}
                    required
                  />
                ) : (
                  <div>
                    <label className="block border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-teal-400 transition bg-slate-800/50">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFile}
                        className="hidden"
                      />
                      {imageBase64 ? (
                        <div>
                          <p className="text-teal-300 font-bold text-sm">
                            ✅ {imageName} loaded
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            Click to choose a different screenshot
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-4xl mb-2">📷</p>
                          <p className="text-white font-bold text-sm">
                            Click to upload a screenshot
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            PNG or JPG, 5MB max. Black out personal details first.
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={analyzing}
                  className="mt-5 w-full px-6 py-3.5 rounded-xl font-extrabold text-blue-900 text-lg shadow-lg disabled:opacity-50 transition"
                  style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
                >
                  {analyzing ? "🦈 Analyzing…" : "🛡️ Analyze"}
                </button>
              </form>
            </div>
          </div>

          {/* verdict */}
          {verdict && v && (
            <div
              ref={verdictRef}
              className={`rounded-3xl p-6 md:p-8 shadow-2xl border-2 ${v.bg} ${v.border}`}
              data-aos="fade-up"
            >
              <p className={`font-extrabold text-2xl ${v.text} mb-4`}>
                {v.icon} SCAM CHECK RESULT — {v.label}
              </p>

              <p className="text-gray-800 font-semibold mb-5">
                {verdict.summary}
              </p>

              {verdict.red_flags?.length > 0 && (
                <>
                  <h3 className="font-bold text-blue-900 mb-2">Why:</h3>
                  <ul className="space-y-1.5 mb-5">
                    {verdict.red_flags.map((f, i) => (
                      <li key={i} className="flex gap-2 text-gray-700 text-sm">
                        <span className="text-blue-900 font-bold">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {verdict.recommended_actions?.length > 0 && (
                <>
                  <h3 className="font-bold text-blue-900 mb-2">
                    Recommended action:
                  </h3>
                  <ul className="space-y-1.5 mb-5">
                    {verdict.recommended_actions.map((a, i) => (
                      <li key={i} className="flex gap-2 text-gray-700 text-sm">
                        <span className="text-red-600 font-bold">→</span> {a}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* escalate */}
              <div className="border-t pt-4 mt-4 text-center">
                <p className="text-gray-600 text-sm mb-3">Still unsure?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/it"
                    className="inline-block px-5 py-2.5 rounded-xl bg-blue-900 text-white text-sm font-bold hover:bg-blue-800 transition"
                  >
                    🦈 Ask LAMNA
                  </Link>
                  <Link
                    to="/contact?request=remote"
                    className="inline-block px-5 py-2.5 rounded-xl border-2 border-blue-900 text-blue-900 text-sm font-bold hover:bg-blue-50 transition"
                  >
                    📋 Open Support Ticket
                  </Link>
                  <a
                    href="tel:8582151048"
                    className="inline-block px-5 py-2.5 rounded-xl border-2 border-blue-900 text-blue-900 text-sm font-bold hover:bg-blue-50 transition"
                  >
                    📞 Request Human Review
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* honesty + privacy notes */}
          <div className="mt-8 text-center text-xs text-gray-400 space-y-2 max-w-xl mx-auto">
            <p>
              🔒 Submitted content is analyzed and{" "}
              <b className="text-gray-300">never stored</b> — nothing is saved
              after you leave this page.
            </p>
            <p>
              ⚖️ Scam Check is an AI second opinion, not a guarantee. Scammers
              evolve — <b className="text-gray-300">when in doubt, don't
              click</b>, and ask a human using the buttons above.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ScamCheck;
